import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { EyeOff, Lock01, Rocket01 } from "@untitledui/icons";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";

/**
 * Vysvětlení, proč je místo obsahu prázdno — funkce je zamčená licencí
 * (LOCKED) nebo teprve připravovaná (ADVERTISED). Nahrazuje stránku
 * `feature-advertised` původního rozhraní.
 *
 * `children` je volitelný slot pro doplňující instrukce ke konkrétní funkci
 * (např. rotace bootstrap hesla na stránce Účet).
 */
export interface FeatureNoticeProps {
  visibility: "ADVERTISED" | "LOCKED";
  featureId: string;
  featureName?: string;
  advertisingText?: string | null;
  plannedVersion?: string | null;
  children?: ReactNode;
}

export function FeatureNotice({
  visibility,
  featureId,
  featureName,
  advertisingText,
  plannedVersion,
  children,
}: FeatureNoticeProps) {
  const { t } = useTranslation();
  const locked = visibility === "LOCKED";

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <div className="flex items-start gap-4">
        <FeaturedIcon
          icon={locked ? Lock01 : Rocket01}
          theme="light"
          color={locked ? "warning" : "brand"}
          size="md"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-md font-semibold text-primary">
              {featureName ?? t(locked ? "feature.locked.title" : "feature.advertised.title")}
            </h2>
            <Badge size="sm" color={locked ? "warning" : "brand"}>
              {t(locked ? "feature.locked.badge" : "feature.advertised.badge")}
            </Badge>
          </div>
          <p className="text-sm text-tertiary">
            {advertisingText ?? t(locked ? "feature.locked.detail" : "feature.advertised.default")}
          </p>
          {plannedVersion && (
            <p className="text-sm text-tertiary">{t("feature.plannedFor", { version: plannedVersion })}</p>
          )}
          <p className="text-xs text-quaternary">
            {t("feature.id")}: <code>{featureId}</code>
          </p>
        </div>
      </div>

      {children}

      <div>
        <Button size="sm" color="secondary">
          <Link to="/settings/license">{t("feature.viewLicense")}</Link>
        </Button>
      </div>
    </div>
  );
}

/**
 * Funkce v této instalaci vůbec není (HIDDEN nebo neznámé id — default-deny).
 * Záměrně neprozrazuje, o kterou funkci jde: HIDDEN nemá existenci potvrzovat.
 */
export function FeatureHiddenNotice() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-start gap-4 rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <div className="flex items-start gap-4">
        <FeaturedIcon icon={EyeOff} theme="light" color="gray" size="md" />
        <div className="flex flex-col gap-1.5">
          <h2 className="text-md font-semibold text-primary">{t("feature.hidden.title")}</h2>
          <p className="text-sm text-tertiary">{t("feature.hidden.detail")}</p>
        </div>
      </div>
      <Button size="sm" color="secondary">
        <Link to="/">{t("feature.backHome")}</Link>
      </Button>
    </div>
  );
}
